import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { CircularProgress, LinearProgress } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/store/authSlice";
import {
  fetchLogsThunk,
  selectLogs,
  selectLogsLoading,
} from "../../logs/store/logsSlice";
import { useBillingPlans } from "../hooks/useBillingPlans";

const TIMEZONE = "America/Los_Angeles";

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseLogDate = (log) => {
  const source = log?._original || log;
  const value = source?.created_at || source?.createdAt || log?.created_time;
  if (!value) return null;

  const parsed = moment.tz(value, TIMEZONE);
  return parsed.isValid() ? parsed : null;
};

const getLogKey = (log) => String(log?.id || log?.job_name || log?.created_time || "");

const mergeLogs = (previousLogs, nextLogs) => {
  const byKey = new Map();

  [...previousLogs, ...nextLogs].forEach((log) => {
    const key = getLogKey(log);
    if (!key) return;
    byKey.set(key, log);
  });

  return Array.from(byKey.values()).sort((a, b) => {
    const dateA = parseLogDate(a);
    const dateB = parseLogDate(b);

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return dateB.valueOf() - dateA.valueOf();
  });
};

const MetricTile = ({ label, value, note, tone = "default", isLoading }) => {
  const toneClass =
    tone === "warning"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : tone === "danger"
        ? "border-red-200 bg-red-50 text-red-700"
        : tone === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-slate-200 bg-slate-50 text-primary";

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${toneClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
      <div className="mt-2 text-2xl font-bold">
        {isLoading ? (
          <span className="flex items-center gap-2 text-xs font-semibold text-muted">
            <CircularProgress size={16} thickness={5} sx={{ color: "#0b1f3a" }} />
            Loading
          </span>
        ) : (
          value
        )}
      </div>
      <p className="mt-1 text-xs opacity-80">
        {isLoading ? "Fetching usage data..." : note}
      </p>
    </div>
  );
};

const UsageLimitTab = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const logs = useSelector(selectLogs);
  const isLoadingLogs = useSelector(selectLogsLoading);

  const { currentPlan, currentClient, isLoadingPlans, plansError } = useBillingPlans();
  const [persistedLogs, setPersistedLogs] = useState([]);
  const lastRequestedRef = useRef(null);

  const resolvedClientId = Number(
    currentClient?.id || currentUser?.client_id || 0,
  );

  useEffect(() => {
    if (!resolvedClientId) return undefined;
    if (lastRequestedRef.current === resolvedClientId) return undefined;
    lastRequestedRef.current = resolvedClientId;
    dispatch(fetchLogsThunk(resolvedClientId));
    return undefined;
  }, [dispatch, resolvedClientId]);

  useEffect(() => {
    if (!resolvedClientId) return undefined;
    setPersistedLogs([]);
    return undefined;
  }, [resolvedClientId]);

  useEffect(() => {
    if (!logs.length) return undefined;

    setPersistedLogs((currentLogs) => mergeLogs(currentLogs, logs));
    return undefined;
  }, [logs]);

  const usageStats = useMemo(() => {
    const now = moment.tz(TIMEZONE);
    const cycleStart = now.clone().startOf("month");
    const cycleEnd = now.clone();

    const sourceLogs = persistedLogs.length ? persistedLogs : logs;

    const datedLogs = sourceLogs
      .map((log) => ({
        log,
        createdAt: parseLogDate(log),
      }))
      .filter((entry) => entry.createdAt);

    const cycleLogs = datedLogs.filter((entry) =>
      entry.createdAt.isSameOrAfter(cycleStart) && entry.createdAt.isSameOrBefore(cycleEnd),
    );

    const weeklyBuckets = Array.from({ length: 7 }, (_, index) => {
      const date = now.clone().subtract(6 - index, "days");
      return {
        key: date.format("YYYY-MM-DD"),
        label: date.format("dd"),
        value: 0,
      };
    });

    cycleLogs.forEach((entry) => {
      const bucketKey = entry.createdAt.format("YYYY-MM-DD");
      const bucket = weeklyBuckets.find((item) => item.key === bucketKey);
      if (bucket) {
        bucket.value += 1;
      }
    });

    const totalAllTime = sourceLogs.length;
    const totalThisMonth = cycleLogs.length;
    const planLimit = Math.max(0, toNumber(currentPlan?.voicemailLimit, 0));
    const remaining = Math.max(planLimit - totalThisMonth, 0);
    const overage = Math.max(totalThisMonth - planLimit, 0);
    const usedPercent = planLimit > 0 ? Math.round((totalThisMonth / planLimit) * 100) : 0;
    const progressValue = planLimit > 0 ? Math.min(100, (totalThisMonth / planLimit) * 100) : 0;
    const peakBar = Math.max(...weeklyBuckets.map((item) => item.value), 1);

    return {
      totalAllTime,
      totalThisMonth,
      planLimit,
      remaining,
      overage,
      usedPercent,
      progressValue,
      cycleLabel: `${cycleStart.format("MMM D, YYYY")} - ${cycleEnd.format("MMM D, YYYY")}`,
      weeklyBuckets: weeklyBuckets.map((item) => ({
        ...item,
        height: Math.max(8, Math.round((item.value / peakBar) * 100)),
      })),
    };
  }, [currentPlan?.voicemailLimit, logs, persistedLogs]);

  const hasUsageData = persistedLogs.length > 0 || logs.length > 0;
  const showLoadingState =
    (isLoadingPlans && !currentPlan) || (isLoadingLogs && !hasUsageData);

  const statusTone = showLoadingState
    ? "neutral"
    : usageStats.overage > 0
      ? "danger"
      : usageStats.usedPercent >= 80
        ? "warning"
        : "success";

  const statusMessage = showLoadingState
    ? "Loading usage data..."
    : usageStats.overage > 0
      ? `Over limit by ${usageStats.overage.toLocaleString()} voicemails`
      : usageStats.usedPercent >= 80
        ? "Approaching plan limit"
        : "Within safe usage range";

  return (
    <div className="sm:mx-2 my-4 rounded-3xl border border-line bg-white p-4 sm:p-6 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-content">Usage Limit</h3>
          <p className="text-sm text-mutedLight">
            Voicemail usage is calculated from the logs created during the current month.
          </p>
        </div>
        <div className="rounded-full border border-primary/10 bg-primary/5 px-4 py-2 text-xs font-semibold text-primary">
          Current cycle: {usageStats.cycleLabel}
        </div>
      </div>

      {(plansError || !resolvedClientId) && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {plansError || "Unable to resolve the active client for usage tracking."}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          label="Plan limit"
          value={usageStats.planLimit ? usageStats.planLimit.toLocaleString() : "Custom"}
          note="Voicemails included in your current plan"
          isLoading={showLoadingState}
        />
        <MetricTile
          label="Used this month"
          value={usageStats.totalThisMonth.toLocaleString()}
          note={`${usageStats.usedPercent}% of plan used`}
          tone={statusTone}
          isLoading={showLoadingState}
        />
        <MetricTile
          label="Remaining"
          value={usageStats.remaining.toLocaleString()}
          note={usageStats.overage > 0 ? "Overage already exceeded" : "Still available this cycle"}
          tone={usageStats.overage > 0 ? "danger" : "success"}
          isLoading={showLoadingState}
        />
        <MetricTile
          label="All-time logs"
          value={usageStats.totalAllTime.toLocaleString()}
          note="Historical voicemail records in the system"
          isLoading={showLoadingState}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4">
        <div className="rounded-2xl border border-line bg-slate-50 p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                <InsightsOutlinedIcon fontSize="small" />
                Usage progress
              </div>
              <p className="mt-1 text-xs text-muted">Compare current month activity with the plan allowance.</p>
            </div>
            <div className={`rounded-full px-3 py-1 text-xs font-semibold ${showLoadingState ? "bg-slate-100 text-slate-600" : statusTone === "danger" ? "bg-red-100 text-red-700" : statusTone === "warning" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {statusMessage}
            </div>
          </div>

          <div className="mt-4">
            <LinearProgress
              variant="determinate"
              value={usageStats.progressValue}
              sx={{
                height: 12,
                borderRadius: 999,
                backgroundColor: "#dbe3ee",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  backgroundColor:
                    statusTone === "danger"
                      ? "#dc2626"
                      : statusTone === "warning"
                        ? "#f59e0b"
                        : "#0b1f3a",
                },
              }}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-white bg-white p-3 shadow-sm">
              <p className="text-xs text-muted">Used %</p>
              <p className="mt-1 text-lg font-bold text-content">
                {showLoadingState ? "--" : `${usageStats.usedPercent}%`}
              </p>
            </div>
            <div className="rounded-xl border border-white bg-white p-3 shadow-sm">
              <p className="text-xs text-muted">Overage</p>
              <p className="mt-1 text-lg font-bold text-content">
                {showLoadingState ? "--" : usageStats.overage.toLocaleString()}
              </p>
            </div>
            <div className="rounded-xl border border-white bg-white p-3 shadow-sm">
              <p className="text-xs text-muted">Tracked client</p>
              <p className="mt-1 text-lg font-bold text-content">
                {showLoadingState ? "--" : resolvedClientId || "-"}
              </p>
            </div>
            <div className="rounded-xl border border-white bg-white p-3 shadow-sm">
              <p className="text-xs text-muted">Cycle scope</p>
              <p className="mt-1 text-lg font-bold text-content">
                {showLoadingState ? "--" : "Monthly"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-line bg-white p-4 sm:p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-primary">
          <CheckCircleOutlineOutlinedIcon fontSize="small" />
          Last 7 days activity
        </div>
        {showLoadingState ? (
          <div className="mt-4 flex h-40 items-center justify-center rounded-xl bg-slate-50">
            <div className="flex items-center gap-2 text-xs text-muted">
              <CircularProgress size={18} thickness={5} sx={{ color: "#0b1f3a" }} />
              Loading activity...
            </div>
          </div>
        ) : (
          <div className="mt-4 grid h-40 grid-cols-7 items-end gap-3">
            {usageStats.weeklyBuckets.map((item) => (
              <div key={item.key} className="flex h-full flex-col items-center justify-end gap-2">
                <div className="flex w-full flex-1 items-end justify-center rounded-xl bg-slate-100 px-1 py-1">
                  <div
                    className="w-full rounded-lg bg-primary/90 transition-all"
                    style={{ height: `${item.height}%` }}
                    aria-hidden
                  />
                </div>
                <div className="text-center">
                  <p className="text-[11px] font-semibold text-content">{item.label}</p>
                  <p className="text-[10px] text-muted">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showLoadingState && (
        <div className="mt-4 text-xs text-muted">
          Loading plan and usage data...
        </div>
      )}

      {usageStats.overage > 0 && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex items-center gap-2 font-semibold">
            <WarningAmberOutlinedIcon fontSize="small" />
            Usage exceeds the current plan by {usageStats.overage.toLocaleString()} voicemails.
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageLimitTab;